import {
  AllWorkspacesDocument,
  RecentWorkspacesDocument,
  Workspace,
  WorkspacesDocument,
  useArchiveWorkspaceMutation,
} from "@gql/sites-api/graphql";
import { ContextMenu, MenuItemProps, MenuItemType, leadElementIconColor } from "react-migration/components/ContextMenu";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import { WorkspaceEventName, logEventV2 } from "react-migration/lib/util/logEventV2";
import { useCallback, useMemo } from "react";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import clsx from "clsx";
import { setEditWorkspaceModal } from "src/js/stores/navigation/actions";
import { showError } from "src/js/stores/user/actions/addNotification";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export type ActionsMenuProps = {
  workspace: Pick<Workspace, "id" | "title" | "developmentType" | "goal" | "image" | "imageUrl">;
  triggerClassName?: string;
  triggerIconClassName?: string;
};

export const ActionsMenu = ({ workspace, triggerClassName, triggerIconClassName }: ActionsMenuProps) => {
  const { t } = useTranslation();

  const [archiveWorkspace] = useArchiveWorkspaceMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllWorkspacesDocument, RecentWorkspacesDocument, WorkspacesDocument],
  });

  const handleEdit = useCallback(() => {
    setEditWorkspaceModal(workspace);
  }, [workspace]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveWorkspace({
        variables: { id: workspace.id },
      });
      logEventV2({
        name: WorkspaceEventName.WORKSPACES_TABLE_WORKSPACE_ARCHIVED,
        properties: { workspaceId: workspace.id },
      });
    } catch (error) {
      console.error("Failed to archive workspace:", error);

      if (!(error instanceof ApolloError)) {
        return;
      }

      const firstGraphQLError = error.graphQLErrors[0];

      if (
        firstGraphQLError?.extensions?.code === "BAD_USER_INPUT" &&
        firstGraphQLError.extensions?.message_code === "cannot_archive_last_remaining_workspace"
      ) {
        showError({
          id: "cannot_archive_last_remaining_workspace",
          title: t("workspaces.error.cannot_archive_last_remaining_workspace.title"),
          description: t("workspaces.error.cannot_archive_last_remaining_workspace.description"),
          duration: 3000,
        });
      }
    }
  }, [archiveWorkspace, workspace.id, t]);

  const menuItems: MenuItemProps[] = useMemo(
    () => [
      {
        type: MenuItemType.ITEM,
        testId: "edit-workspace",
        label: t("workspaces.table.actions.edit"),
        leadElement: <Icon icon="icon-lt-edit-line" extraClasses={leadElementIconColor} />,
        onClick: handleEdit,
      },
      {
        type: MenuItemType.ITEM,
        testId: "archive-workspace",
        label: t("workspaces.table.actions.archive"),
        leadElement: <Icon icon="icon-lt-archive-line" extraClasses={leadElementIconColor} />,
        onClick: handleArchive,
      },
    ],
    [t, handleEdit, handleArchive]
  );

  return (
    <ContextMenu
      menuItems={menuItems}
      triggerTestId="actions-menu-trigger"
      triggerIconProps={{
        icon: "icon-lt-more-2-line",
        size: "sm",
        extraClasses: clsx("text-neutral-800", triggerIconClassName),
        cursor: "pointer",
      }}
      triggerClassName={triggerClassName}
    />
  );
};
